import React, { useState, useEffect } from 'react';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import idScan from './assets/id-scan.json';

import Typography from '@mui/material/Typography';
import RadioGroup from '../components/RadioGroup.jsx';
import AsyncAutoComplete from '../components/AsyncAutoComplete.jsx';

import ContentContainer from '../layout/ContentContainer.jsx';

import { getTextDefaults } from './translations/socSecuritySectionTranslations';
import { getFlagMessages } from './translations/documentFlagMessages';
import { hasUnacknowledgedReview } from '../util/documentProblems.js';
import { isEerCountry } from '../util/countries.js';
import usePreviousImmediate from '@rooks/use-previous-immediate';
import DocumentsPage from './components/DocumentsPage.jsx';
import { Link } from '@mui/material';

const CONSTANTS = {
	radio: {
		exceptionRadio: 'exception',
	},
	screens: {
		welcome: 'welcome',
		nationalitySelection: 'nationality-selection',
		exceptionSelection: 'exception-selection',
		durationSelection: 'duration-selection',
		workDocuments: 'work-documents',
		finished: 'finished',
	},
	state: {
		nationality: 'nationality',
		exception: 'exception',
		duration: 'duration',
	},
};

const docLabels = (selectedLanguage) => {
	return {
		passport: 'Passport',
		visa: 'Visa',
		'nl-gvva': 'GVVA (combined residence and work permit)',
		'nl-twv': 'TWV (work permit)',
		'any-id': 'National ID or Passport',
		'residence-permit': 'Residence Permit',
		'nl-notification': selectedLanguage === 'NL' ? 'Meldingsplicht WagwEU' : 'Notification WagwEU',
		a1: 'A1',
	};
};

const isEerCountryOrSwiss = (country) => {
	return isEerCountry(country);
};

const calculateRequiredDocuments = (values) => {
	const nationality = values[CONSTANTS.state.nationality];
	const exception = values[CONSTANTS.state.exception];
	const duration = values[CONSTANTS.state.duration];

	if (isEerCountryOrSwiss(nationality)) {
		const requiredDocuments = ['any-id'];
		if (nationality === 'NL') {
			return requiredDocuments;
		}
		requiredDocuments.push('nl-notification', 'a1');
		return requiredDocuments;
	} else {
		const requiredDocuments = ['passport', 'visa'];
		if (exception === 'yes') {
			requiredDocuments.push('nl-notification');
			return requiredDocuments;
		}

		// if visit is not covered under an exception we require nl-notification
		requiredDocuments.push('nl-notification');
		// more than 90 days
		if (duration === 'yes') {
			requiredDocuments.push('nl-gvva');
		} else {
			requiredDocuments.push('nl-twv');
		}
		return requiredDocuments;
	}
};

const calculatePossibleDocuments = (values) => {
	const nationality = values[CONSTANTS.state.nationality];

	if (isEerCountryOrSwiss(nationality)) {
		if (nationality === 'NL') {
			// Only optional for NL
			return ['nl-notification', 'a1'];
		}
	} else {
		return ['residence-permit'];
	}
};

const hasMissingDocuments = (requiredDocumentTypes, submittedDocs) => {
	if (!Array.isArray(requiredDocumentTypes) || !Array.isArray(submittedDocs)) {
		return true;
	}

	const missingDocuments = requiredDocumentTypes.filter(
		(requiredType) =>
			!submittedDocs?.find((submittedDoc) => {
				if (submittedDoc?.type === requiredType) return true;
				if (requiredType === 'any-id' && ['id', 'passport'].includes(submittedDoc?.type)) return true;
				return false;
			}),
	);

	return missingDocuments.length > 0;
};

const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen animation={idScan}>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.welcomeTitle}
			</Typography>
			<Typography>{text.welcomeReason}</Typography>
		</WelcomeScreen>
	);
};

const FinishedPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.finishedTitle}
			</Typography>
			<Typography>{text.finishedReason}</Typography>
		</WelcomeScreen>
	);
};

const SocSecuritySectionNL = ({
	onChange = () => {},
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	disabled = false,
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	style = {},
	...rest
}) => {
	const [currentScreen, setCurrentScreen] = useState(CONSTANTS.screens.welcome);
	const previousCurrentScreen = usePreviousImmediate(currentScreen);
	const requiredDocuments = calculateRequiredDocuments(values);
	const possibleDocuments = calculatePossibleDocuments(values);
	let textDefaults = getTextDefaults(selectedLanguage);
	let text = Object.assign({}, textDefaults, textOverride);

	useEffect(() => {
		const userCompanyLocation = values?.userData?.groupCountry;
		// Set company location if company info is available on user
		if (userCompanyLocation) {
			values[CONSTANTS.state.nationality] = userCompanyLocation;
		}
	}, []);

	const radioOptions = [
		{
			value: 'yes',
			label: text.yes,
		},
		{
			value: 'no',
			label: text.no,
		},
	];

	const handleNext = async (event) => {
		if (currentScreen === CONSTANTS.screens.welcome) {
			return setCurrentScreen(CONSTANTS.screens.nationalitySelection);
		}

		if (currentScreen === CONSTANTS.screens.nationalitySelection) {
			if (isEerCountryOrSwiss(values[CONSTANTS.state.nationality]))
				return setCurrentScreen(CONSTANTS.screens.workDocuments);
			return setCurrentScreen(CONSTANTS.screens.exceptionSelection);
		}

		if (currentScreen === CONSTANTS.screens.exceptionSelection) {
			if (values[CONSTANTS.state.exception] === 'yes') return setCurrentScreen(CONSTANTS.screens.workDocuments);
			return setCurrentScreen(CONSTANTS.screens.durationSelection);
		}

		if (currentScreen === CONSTANTS.screens.durationSelection) {
			return setCurrentScreen(CONSTANTS.screens.workDocuments);
		}

		if (currentScreen === CONSTANTS.screens.workDocuments) {
			return setCurrentScreen(CONSTANTS.screens.finished);
		}

		if (currentScreen === CONSTANTS.screens.finished) {
			return navigationProps.next(event);
		}
	};

	const handlePrevious = (event) => {
		if (
			previousCurrentScreen === CONSTANTS.screens.exceptionConfirmation &&
			currentScreen === CONSTANTS.screens.finished
		) {
			return setCurrentScreen(CONSTANTS.screens.exceptionConfirmation);
		}

		if (currentScreen === CONSTANTS.screens.finished) {
			setCurrentScreen(CONSTANTS.screens.workDocuments);
		}

		if (currentScreen === CONSTANTS.screens.workDocuments) {
			if (values[CONSTANTS.state.exception] === 'no') return setCurrentScreen(CONSTANTS.screens.durationSelection);
			if (!isEerCountryOrSwiss(values[CONSTANTS.state.nationality]))
				return setCurrentScreen(CONSTANTS.screens.exceptionSelection);
			return setCurrentScreen(CONSTANTS.screens.nationalitySelection);
		}

		if (currentScreen === CONSTANTS.screens.durationSelection) {
			setCurrentScreen(CONSTANTS.screens.exceptionSelection);
		}

		if (currentScreen === CONSTANTS.screens.exceptionSelection) {
			setCurrentScreen(CONSTANTS.screens.nationalitySelection);
		}

		if (currentScreen === CONSTANTS.screens.nationalitySelection) {
			setCurrentScreen(CONSTANTS.screens.welcome);
		}

		if (currentScreen === CONSTANTS.screens.welcome) {
			return navigationProps.previous(event);
		}
	};

	const handleReset = () => {
		setCurrentScreen(CONSTANTS.screens.nationalitySelection);
		onChange({ target: { name: 'userDocuments', value: [] } });
	};

	let canContinue = false;
	if (currentScreen === CONSTANTS.screens.welcome || currentScreen === CONSTANTS.screens.finished) canContinue = true;

	if (currentScreen === CONSTANTS.screens.nationalitySelection) canContinue = !!values[CONSTANTS.state.nationality];

	if (currentScreen === CONSTANTS.screens.exceptionSelection) canContinue = !!values[CONSTANTS.state.exception];

	if (currentScreen === CONSTANTS.screens.durationSelection) canContinue = !!values[CONSTANTS.state.duration];

	if (currentScreen === CONSTANTS.screens.workDocuments)
		canContinue =
			!hasMissingDocuments(requiredDocuments, values.userDocuments) &&
			!hasUnacknowledgedReview(values.userDocuments);

	const hasPrevious =
		navigationProps.hasPrevious ||
		currentScreen === CONSTANTS.screens.nationalitySelection ||
		currentScreen === CONSTANTS.screens.exceptionSelection ||
		currentScreen === CONSTANTS.screens.durationSelection ||
		currentScreen === CONSTANTS.screens.workDocuments ||
		currentScreen === CONSTANTS.screens.finished;

	useEffect(() => {
		if (currentScreen === CONSTANTS.screens.nationalitySelection) handleReset();
	}, [currentScreen]);

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={hasPrevious}
					hasNext={currentScreen !== CONSTANTS.screens.finished || navigationProps.hasNext}
					canContinue={canContinue}
					next={handleNext}
					previous={handlePrevious}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{currentScreen === CONSTANTS.screens.welcome && <WelcomePage text={text} />}
			{currentScreen === CONSTANTS.screens.nationalitySelection && (
				<ContentContainer>
					<Typography variant="h6">{text.nationalityText}</Typography>
					<AsyncAutoComplete
						name={CONSTANTS.state.nationality}
						value={values[CONSTANTS.state.nationality]}
						onChange={onChange}
						required={true}
						dataSource="/api/data-sources/countries/data"
						placeholder={text.nationalityPlaceholder}
						instantSearch={true}
					/>
				</ContentContainer>
			)}
			{currentScreen === CONSTANTS.screens.exceptionSelection && (
				<ContentContainer>
					<Typography variant="h6">{text.exceptionSelectionText}</Typography>
					<RadioGroup
						name={CONSTANTS.state.exception}
						value={values[CONSTANTS.state.exception]}
						options={radioOptions}
						onChange={onChange}
						disabled={disabled}
					/>
					<Typography
						variant="caption"
						sx={{
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
							maxWidth: '100%',
							display: 'block',
							color: 'rgba(0, 0, 0, 0.6)',
						}}
					>
						{text.scanningHelperText + ': '}
						<Link
							sx={{ color: 'inherit', '&:hover': { color: 'rgba(0, 0, 0, 0.6)' } }}
							href="https://www.government.nl/topics/foreign-citizens-working-in-the-netherlands/question-and-answer/what-permits-do-foreign-workers-need"
							target="_blank"
						>
							government.nl
						</Link>
					</Typography>
				</ContentContainer>
			)}
			{currentScreen === CONSTANTS.screens.durationSelection && (
				<ContentContainer>
					<Typography variant="h6">{text.durationSelectionText}</Typography>
					<RadioGroup
						name={CONSTANTS.state.duration}
						value={values[CONSTANTS.state.duration]}
						options={radioOptions}
						onChange={onChange}
						disabled={disabled}
					/>
				</ContentContainer>
			)}
			{currentScreen === CONSTANTS.screens.workDocuments && (
				<DocumentsPage
					value={values.userDocuments}
					requiredDocuments={requiredDocuments}
					possibleDocuments={possibleDocuments}
					labels={docLabels(selectedLanguage)}
					text={text}
					onChange={onChange}
					disabled={disabled}
					name="userDocuments"
					useCamera={useCamera}
					cameraIncludes={cameraIncludes}
					cameraRotationAngle={cameraRotationAngle}
					helperText={text.scanningHelperText}
					enableReviewFeedback
					reviewMessages={getFlagMessages(selectedLanguage)}
					{...rest}
				/>
			)}

			{currentScreen === CONSTANTS.screens.finished && <FinishedPage text={text} />}
		</SectionContainer>
	);
};

SocSecuritySectionNL.type = 'SocSecuritySectionNL';
SocSecuritySectionNL.hasNavigation = true;
SocSecuritySectionNL.spec = [
	{
		name: 'SocSecuritySectionNL',
		key: 'SocSecuritySectionNL',
		title: 'Dutch social security section',
		translatable: ['navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		allowedFields: [],
		fields: [
			{
				type: 'Checkbox',
				name: 'useCamera',
				label: 'Force camera only (kiosk environments only — requires device to be registered)',
			},
		],
		defaultProperties: {
			refDataMap: {
				'userData.groupCountry': 'user.userData.groupCountry',
			},
		},
	},
];

export default SocSecuritySectionNL;
